import { useState, useCallback, useEffect, useRef } from 'react';
import { Drawer } from 'antd';
import { useNavigate } from 'react-router-dom';

import SelectedItems from './SelectedItems';
import SaveButton from './SaveButton';
import SearchBar from './SearchBar';
import request from '@/request';

export default function ItemSelector({
  open,
  onClose,
  onConfirm,
  name = '',
  selectedItems = [],
  onToggle = () => {},
  okText = '确定',
  api = '',
  size = 450,
  options = [],
  itemComponent = <></>,
  multiple,
  addPath = '',
  onCreate,
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const listRef = useRef();
  const loadMoreRef = useRef();
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const selectedIds = selectedItems.map((item) => item.id);

  // 加载数据并合并
  const loadData = useCallback(
    async (params = '') => {
      if (!api) return;
      setLoading(true);
      const res = await request.get(
        `${api}${api.indexOf('?') === -1 ? '?' : '&'}page=${page}&pageSize=${pageSize}&${params}`
      );
      const newItems = res.items || [];
      setItems((prev) => (page === 1 ? newItems : [...prev, ...newItems]));
      setHasMore(newItems.length === pageSize);
      setLoading(false);
    },
    [api, page, pageSize]
  );

  useEffect(() => {
    if (open) loadData();
    else {
      setItems([]);
      setPage(1);
      setHasMore(true);
    }
  }, [open, loadData]);

  // IntersectionObserver方式加载下一页
  useEffect(() => {
    if (!hasMore || loading) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { root: null, threshold: 0.1 }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  return (
    <Drawer
      title={`选择${name}`}
      size={size}
      onClose={onClose}
      open={open}
      extra={
        <SaveButton
          text={`${okText}${selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}`}
          onSave={onConfirm}
          disabled={selectedItems.length === 0}
        />
      }
    >
      <div className="space-y-3">
        <div className="sticky top-0 bg-white z-10 space-y-3  pb-3">
          <div className="h-6 absolute -top-6 left-0 right-0 bg-white"></div>
          {multiple && (
            <SelectedItems
              items={selectedItems}
              onRemove={(value) => onToggle(value, multiple)}
            />
          )}
          <SearchBar
            options={options}
            onSearch={loadData}
            onCreate={onCreate}
            searchOnShow={false}
          />
        </div>
        <div className="space-y-2" ref={listRef}>
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 border border-slate-200 rounded-lg bg-slate-50">
              <svg
                className="w-32 h-32 text-slate-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <div className="text-slate-500 text-sm font-medium">
                <span className="">暂无数据</span>
                {addPath && (
                  <button
                    onClick={() => navigate(addPath)}
                    className="ml-1 text-blue-600 hover:underline"
                  >
                    去添加
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {items.map((it) => {
                const checked = selectedIds.includes(it.id);
                const ItemComponent = itemComponent;
                return (
                  <label
                    key={it.id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer border transition-all duration-200 ${
                      checked
                        ? 'bg-blue-50 border-blue-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => onToggle(it, multiple)}
                  >
                    {<ItemComponent key={it.id} item={it} checked={checked} />}
                    <div className="flex-shrink-0">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          checked
                            ? 'bg-blue-600 border-blue-600'
                            : 'bg-white border-slate-300'
                        }`}
                      >
                        {checked && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
              {/* 加载中效果 */}
              {loading && (
                <div className="flex items-center justify-center py-4 text-slate-400">
                  <svg
                    className="animate-spin w-6 h-6 mr-2"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 01-8 8z"
                    />
                  </svg>
                  加载中...
                </div>
              )}
              {/* 没有更多数据效果 */}
              {!hasMore && items.length > 0 && (
                <div className="flex items-center justify-center py-2 text-slate-400 text-sm">
                  没有更多数据了
                </div>
              )}
              {/* 触发加载更多的占位元素 */}
              {hasMore && <div ref={loadMoreRef} style={{ height: 1 }} />}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}
